import { Alert, Box, Button, Chip, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { AddOutlined, DeleteOutline, VisibilityOutlined } from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { SuperAdminSurveyTemplateService } from '../../../../shared/services/survey-template/survey-template.service';
import { ISurveyTemplate } from '../../../../shared/services/survey-template/survey-template.model';
import { ILibraryQuestion } from '../../../../shared/services/library-question-group/library-question-group.model';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../../loading-page';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { GroupItemVisibilityDialog } from '../../../dialogs/surveys/group-item-visibility-dialog';
import { SurveyQuestionDialog } from '../../../dialogs/surveys/survey-question-dialog';
import { SurveyCalculationsPanel } from '../../../presentation-components/surveys/builder/survey-calculations-panel';
import { SuperAdminSurveyTemplateCreateDialog } from '../../../dialogs/super-admin/super-admin-survey-template-create-dialog';
import { addPageBreakUpdates, removePageBreakUpdates, sortItemsByPage } from '../../../../shared/utils/survey-rules/group-pages';

interface TabPanelProps { children: React.ReactNode; value: number; index: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

// Authoring one platform survey template, laid out like the client question-set view: a publish
// bar, then Questions / Calculations tabs. The Calculations tab is the shared builder panel,
// hosted through an injected api pointed at the templates service instead of the client tables.
export const SuperAdminSurveyTemplateDetailPage = () => {
  const service = new SuperAdminSurveyTemplateService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const { id } = useParams();

  const [group, setGroup] = useState<ISurveyTemplate>();
  const [questionBank, setQuestionBank] = useState<ILibraryQuestion[]>([]);
  const [questionTypes, setQuestionTypes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<ILibraryQuestion | null>(null);
  const [addAfterCreate, setAddAfterCreate] = useState(false);
  const [bankIdsBeforeCreate, setBankIdsBeforeCreate] = useState<Set<string>>(new Set());
  const [visibilityItem, setVisibilityItem] = useState<any>(null);

  const load = () => {
    if (!id) return;
    Promise.all([service.getOne(id), service.getAllQuestions(), service.getQuestionTypes()])
      .then(([loaded, bank, types]) => {
        setGroup(loaded);
        setQuestionBank(bank);
        setQuestionTypes(types);
      })
      .catch(err => {
        console.log(err);
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(load, [id]);

  const notifyError = (err: any) => {
    userNotificationsContext.openSnackbar({
      open: true,
      message: err?.response?.data?.message || 'There was an error, please try again',
      backgroundColor: 'text.primary',
    });
    console.log(err);
  };

  const togglePublished = () => {
    if (!id || !group) return;
    (group.isPublished ? service.unpublish(id) : service.publish(id))
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message: group.isPublished ? 'Template unpublished' : 'Template published', backgroundColor: 'primary.dark' });
        load();
      })
      .catch(notifyError);
  };

  // A question created from inside this template is added to it straight away; one edited from
  // here is not re-added.
  const questionSaved = () => {
    setQuestionDialogOpen(false);
    const shouldAdd = addAfterCreate;
    setAddAfterCreate(false);
    setEditingQuestion(null);
    if (!id || !shouldAdd) { load(); return; }
    service.getAllQuestions()
      .then(bank => {
        const created = bank.find(q => !bankIdsBeforeCreate.has(q.id));
        if (!created) { load(); return; }
        return service.addItem(id, { libraryQuestionId: created.id, sortOrder: items.length, pageNumber: lastPage }).then(load);
      })
      .catch(notifyError);
  };

  const addExistingQuestion = (questionId: string) => {
    if (!id) return;
    service.addItem(id, { libraryQuestionId: questionId, sortOrder: items.length, pageNumber: lastPage })
      .then(load).catch(notifyError);
  };

  const addPageBreak = (afterItem: any) => {
    if (!id) return;
    const updates = addPageBreakUpdates(items, afterItem);
    if (updates.length === 0) return;
    service.updateItemPages(id, updates).then(load).catch(notifyError);
  };

  const removePageBreak = (pageNum: number) => {
    if (!id) return;
    const updates = removePageBreakUpdates(items, pageNum);
    if (updates.length === 0) return;
    service.updateItemPages(id, updates).then(load).catch(notifyError);
  };

  const removeItem = (item: any) => {
    if (!id) return;
    userNotificationsContext.openDialog({
      active: true, title: 'Remove question',
      message: `Remove "${item.libraryQuestion?.prompt}" from this template?`,
      buttonCopy: 'Remove', cancelButtonCopy: 'Cancel',
    }).then(ok => { if (ok) service.removeItem(id, item.id).then(load).catch(notifyError); });
  };

  const items = sortItemsByPage(group?.libraryQuestionGroupItems || []) as any[];
  const pageNumbers = [...new Set(items.map(i => i.pageNumber ?? 1))].sort((a, b) => a - b);
  if (pageNumbers.length === 0) pageNumbers.push(1);
  const itemsByPage = new Map<number, typeof items>();
  for (const item of items) {
    const page = item.pageNumber ?? 1;
    itemsByPage.set(page, [...(itemsByPage.get(page) ?? []), item]);
  }
  const lastPage = pageNumbers[pageNumbers.length - 1];
  const allRules = group?.libraryQuestionGroupEmissionRules || [];
  const problems = group?.integrityProblems || [];

  // The panels and dialogs speak the client-side shape; a template item carries its question
  // under a different association name.
  const groupItemsForDialog = items.map(item => ({
    ...item,
    surveyQuestionId: item.libraryQuestionId,
    surveyQuestion: item.libraryQuestion
      ? { ...item.libraryQuestion, surveyQuestionOptions: item.libraryQuestion.libraryQuestionOptions || [] }
      : undefined,
  })) as any[];

  const alreadyAdded = new Set(items.map(i => i.libraryQuestionId));

  const PageItemRow = ({ item }: { item: any }) => (
    <Stack direction='row' alignItems='center' spacing={1} sx={{ py: 0.5 }}>
      <Typography variant='body2' sx={{ flex: 1 }}>{item.libraryQuestion?.prompt}</Typography>
      {(item.libraryQuestion?.libraryQuestionOptions?.length || 0) > 0 &&
        <Chip size='small' variant='outlined' label={`${item.libraryQuestion?.libraryQuestionOptions?.length} options`} />}
      {item.visibilityRule &&
        <Chip size='small' variant='outlined' color='primary' label='Conditional' />}
      <Button size='small' onClick={() => { setEditingQuestion(item.libraryQuestion || null); setAddAfterCreate(false); setQuestionDialogOpen(true); }}>
        Edit
      </Button>
      <Button size='small' startIcon={<VisibilityOutlined fontSize='small' />} onClick={() => setVisibilityItem(item)}>
        Display condition
      </Button>
      <Button size='small' color='inherit' startIcon={<DeleteOutline fontSize='small' />} onClick={() => removeItem(item)}>
        Remove
      </Button>
    </Stack>
  );

  return (
    <PageOuterContainer>
      <LoadingPage pageReady={!isLoading}>
        <PageHeading
          title={group?.name ?? 'Survey template'}
          includeBackButton
          backButtonText='Back to templates'
          backButtonLink='/super-admin/survey-templates'
          includeActionButton
          actionButtonText='Edit details'
          actionButtonIcon={IconName.EditOutlined}
          actionButtonHandler={() => setEditDetailsOpen(true)}
        />

        {group?.description && (
          <Typography color='text.secondary' sx={{ mb: 2 }}>{group.description}</Typography>
        )}

        {/* ---- publish bar ---- */}
        <Box sx={{ p: 1.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: '10px', bgcolor: 'background.paper' }}>
          <Stack direction='row' alignItems='center' spacing={2}>
            <Chip label={group?.isPublished ? 'Published' : 'Draft'} color={group?.isPublished ? 'success' : 'default'} size='small' />
            <Typography variant='body2' sx={{ color: 'text.secondary', flex: 1 }}>
              {group?.isPublished
                ? 'Clients can start a survey from this template.'
                : 'Draft templates are not offered to clients. Publish when it is ready.'}
            </Typography>
            <Button
              variant={group?.isPublished ? 'outlined' : 'contained'}
              onClick={togglePublished}
              disabled={!group?.isPublished && problems.length > 0}
            >
              {group?.isPublished ? 'Unpublish' : 'Publish'}
            </Button>
          </Stack>
        </Box>

        {problems.length > 0 && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <Typography variant='body2' sx={{ mb: 0.5 }}>This template can't be published until these are fixed:</Typography>
            {problems.map((p, i) => <Typography key={i} variant='caption' sx={{ display: 'block' }}>• {p.message}</Typography>)}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1 }}>
          <Tab label={`Questions (${items.length})`} />
          <Tab label={`Calculations (${allRules.length})`} />
        </Tabs>
        <Divider />

        {/* ---- questions ---- */}
        <TabPanel value={tabValue} index={0}>
          {items.length === 0 &&
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>No questions yet.</Typography>}

          {pageNumbers.map((pageNum, pageIdx) => {
            const pageItems = itemsByPage.get(pageNum) ?? [];
            if (pageItems.length === 0) return null;
            return (
              <Box key={pageNum} sx={{ mb: 2 }}>
                {pageNumbers.length > 1 && (
                  <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant='overline' sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                      Page {pageNum} of {pageNumbers.length}
                    </Typography>
                    {pageIdx > 0 && (
                      <Button size='small' variant='text' onClick={() => removePageBreak(pageNum)}
                        sx={{ fontSize: '0.7rem', color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                        remove page break
                      </Button>
                    )}
                  </Stack>
                )}
                <Stack spacing={1}>
                  {pageItems.map((item, itemIdx) => (
                    <Box key={item.id}>
                      <PageItemRow item={item} />
                      {!(pageIdx === pageNumbers.length - 1 && itemIdx === pageItems.length - 1) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.25 }}>
                          <Button size='small' variant='text' onClick={() => addPageBreak(item)}
                            sx={{ fontSize: '0.7rem', color: 'text.disabled', minWidth: 0, px: 1, '&:hover': { color: 'primary.main' } }}>
                            + page break
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            );
          })}

          <Box sx={{ mb: 2 }}>
            <Button variant='outlined' startIcon={<AddOutlined />}
              onClick={() => {
                setEditingQuestion(null);
                setAddAfterCreate(true);
                setBankIdsBeforeCreate(new Set(questionBank.map(q => q.id)));
                setQuestionDialogOpen(true);
              }}>
              New question
            </Button>
          </Box>

          {questionBank.filter(q => !alreadyAdded.has(q.id)).length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                Or reuse a question from another template:
              </Typography>
              <Stack direction='row' sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {questionBank.filter(q => !alreadyAdded.has(q.id)).map(q => (
                  <Chip key={q.id} size='small' label={q.prompt} onClick={() => addExistingQuestion(q.id)} />
                ))}
              </Stack>
            </Box>
          )}
        </TabPanel>

        {/* ---- calculations (emission rules + intermediate values) ---- */}
        <TabPanel value={tabValue} index={1}>
          {id && (
            <SurveyCalculationsPanel
              groupId={id}
              groupItems={groupItemsForDialog}
              rules={allRules as any[]}
              onChanged={load}
              api={{
                createEmissionRule: (gid, payload) => service.createRule(gid, payload),
                updateEmissionRule: (gid, rid, payload) => service.editRule(gid, rid, payload),
                deleteEmissionRule: (gid, rid) => service.removeRule(gid, rid),
              }}
            />
          )}
        </TabPanel>
      </LoadingPage>

      {group && (
        <SuperAdminSurveyTemplateCreateDialog
          open={editDetailsOpen}
          onClose={() => setEditDetailsOpen(false)}
          onSuccess={() => { setEditDetailsOpen(false); load(); }}
          existing={group}
        />
      )}

      {questionDialogOpen &&
        <SurveyQuestionDialog
          open={questionDialogOpen}
          onClose={() => { setQuestionDialogOpen(false); setEditingQuestion(null); setAddAfterCreate(false); }}
          successHandler={questionSaved}
          questionTypes={questionTypes}
          existingQuestion={editingQuestion as any}
          api={{
            createQuestion: (payload) => service.createQuestion(payload),
            updateQuestion: (qid, payload) => service.editQuestion(qid, payload),
            addOption: (qid, payload) => service.addOption(qid, payload),
            deleteOption: (qid, optionId) => service.deleteOption(qid, optionId),
          }}
        />}

      {visibilityItem && id &&
        <GroupItemVisibilityDialog
          open={Boolean(visibilityItem)}
          onClose={() => setVisibilityItem(null)}
          successHandler={() => { setVisibilityItem(null); load(); }}
          groupId={id}
          item={{ ...visibilityItem, surveyQuestionId: visibilityItem.libraryQuestionId }}
          precedingItems={groupItemsForDialog.slice(0, items.findIndex(i => i.id === visibilityItem.id))}
          api={{ updateGroupItem: (gid, itemId, payload) => service.editItem(gid, itemId, payload) }}
        />}
    </PageOuterContainer>
  );
};
